import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  closeTopOverlay,
  handleOverlayHistoryPop,
} from '../../utils/backNavigation';
import { showToast } from '../../utils/toast';

/**
 * SmartBackHandler — mount ONCE inside the Router.
 *
 * - Browser back / Android swipe-back / touch back gesture (popstate):
 *   closes the topmost overlay first; otherwise lets the router go back
 *   one page normally. One press = one step, always.
 * - Escape key: closes the topmost overlay (single global handler so
 *   stacked modals close one-per-press instead of all at once).
 * - Cordova / Capacitor native back button (when present): same
 *   overlay-first behaviour, plus a double-press-to-exit guard on root
 *   routes so users don't accidentally kill the installed app.
 */
const SmartBackHandler = () => {
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;
  const lastExitPressRef = useRef(0);

  // Browser / swipe-back gesture.
  useEffect(() => {
    const onPopState = () => {
      handleOverlayHistoryPop();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Escape closes one overlay layer per press.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Don't hijack Escape while typing in a select/combobox popup owned
      // by the browser — only act when an app overlay is actually open.
      if (closeTopOverlay()) e.preventDefault();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Native back button (Cordova `backbutton` + Capacitor App plugin).
  useEffect(() => {
    const isRootRoute = () => {
      const p = locationRef.current || '/';
      return p === '/' || p === '/dashboard' || p === '/login';
    };

    const tryExitApp = () => {
      try {
        const capApp = window?.Capacitor?.Plugins?.App;
        if (capApp && typeof capApp.exitApp === 'function') {
          capApp.exitApp();
          return true;
        }
      } catch {
        /* not running under Capacitor */
      }
      return false;
    };

    // Cordova-style event.
    const onNativeBack = (e) => {
      if (closeTopOverlay()) {
        try {
          e?.preventDefault?.();
        } catch {
          /* noop */
        }
        return;
      }
      if (isRootRoute()) {
        const now = Date.now();
        if (now - lastExitPressRef.current < 2000) {
          if (!tryExitApp() && window.history.length > 1) {
            try {
              window.history.back();
            } catch {
              /* noop */
            }
          }
        } else {
          lastExitPressRef.current = now;
          showToast('Press back again to exit', { type: 'info', duration: 2000 });
          try {
            e?.preventDefault?.();
          } catch {
            /* noop */
          }
        }
      }
      // Non-root with no overlay: let the OS do the normal page back.
    };

    document.addEventListener('backbutton', onNativeBack);

    // Capacitor App plugin event (Capacitor apps also fire popstate, but
    // the plugin event fires first and lets us preventDefault cleanly).
    let capHandle = null;
    let cancelled = false;
    try {
      const capApp = window?.Capacitor?.Plugins?.App;
      if (capApp && typeof capApp.addListener === 'function') {
        capApp.addListener('backButton', onNativeBack).then(
          (h) => {
            if (cancelled) {
              try {
                h?.remove?.();
              } catch {
                /* noop */
              }
            } else {
              capHandle = h;
            }
          },
          () => {}
        );
      }
    } catch {
      /* not running under Capacitor */
    }

    return () => {
      cancelled = true;
      document.removeEventListener('backbutton', onNativeBack);
      try {
        capHandle?.remove?.();
      } catch {
        /* noop */
      }
    };
  }, []);

  return null;
};

export default SmartBackHandler;
